/**
 * Shift type detection, colour coding, and symbols.
 *
 * Dynamic per nursery: each nursery's configured opening and closing times
 * (from Settings → Nursery Configuration) drive how shifts are classified.
 * The constants below are FALLBACK defaults used only until the live
 * operating hours have been loaded, they must not be used for input
 * validation. Shift time pickers are fully uncapped (B4-02).
 */

export const NURSERY_OPEN = "07:30";
export const NURSERY_CLOSE = "18:30";
export const OPS_OPEN = "07:00";
export const OPS_CLOSE = "20:00";

export type ShiftType = "starter" | "finisher" | "mid_day" | "full_day" | "day_off";

export interface OperatingHours {
  /** Childcare opening time, HH:MM */
  open: string;
  /** Childcare closing time, HH:MM */
  close: string;
  /** Operations earliest shift start, HH:MM */
  opsOpen: string;
  /** Operations latest shift end, HH:MM */
  opsClose: string;
}

export interface ShiftTypeInfo {
  type: ShiftType;
  label: string;
  symbol: string;
  /** Hex colour for on-screen display */
  color: string;
  /** Foreground colour */
  fg: string;
  /** Tailwind-safe inline style for bg + text */
  style: React.CSSProperties;
  /** Print-friendly pattern description */
  printPattern: string;
}

const TYPES: Record<ShiftType, Omit<ShiftTypeInfo, "type">> = {
  starter: {
    label: "Starter",
    symbol: "▶",
    color: "#016131",
    fg: "#ffffff",
    style: { backgroundColor: "#016131", color: "#ffffff" },
    printPattern: "diagonal-stripes",
  },
  finisher: {
    label: "Finisher",
    symbol: "◀",
    color: "#EA1E2A",
    fg: "#ffffff",
    style: { backgroundColor: "#EA1E2A", color: "#ffffff" },
    printPattern: "dotted",
  },
  mid_day: {
    label: "Mid-Day",
    symbol: "◆",
    color: "#08A3E3",
    fg: "#ffffff",
    style: { backgroundColor: "#08A3E3", color: "#ffffff" },
    printPattern: "horizontal-lines",
  },
  full_day: {
    label: "Full Day",
    symbol: "★",
    color: "#97488A",
    fg: "#ffffff",
    style: { backgroundColor: "#97488A", color: "#ffffff" },
    printPattern: "cross-hatch",
  },
  day_off: {
    label: "Day Off",
    symbol: "",
    color: "#e5e7eb",
    fg: "#6b7280",
    style: { backgroundColor: "#e5e7eb", color: "#6b7280" },
    printPattern: "none",
  },
};

// ─── Active operating hours (module-level state) ─────────────────────────
// Set once at app load by a top-level effect that reads the current
// nursery's config. All classification helpers default to these if no
// explicit OperatingHours argument is passed.
let activeHours: OperatingHours = {
  open: NURSERY_OPEN,
  close: NURSERY_CLOSE,
  opsOpen: OPS_OPEN,
  opsClose: OPS_CLOSE,
};

export function setActiveOperatingHours(next: Partial<OperatingHours>): void {
  activeHours = {
    open: next.open?.slice(0, 5) || activeHours.open,
    close: next.close?.slice(0, 5) || activeHours.close,
    opsOpen: next.opsOpen?.slice(0, 5) || activeHours.opsOpen,
    opsClose: next.opsClose?.slice(0, 5) || activeHours.opsClose,
  };
}

export function getActiveOperatingHours(): OperatingHours {
  return activeHours;
}

// ─── Helpers ─────────────────────────────────────────────────────────────
function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.slice(0, 5).split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Format HH:MM in 24h to a human label, e.g. "07:30". */
export function formatTimeLabel(hhmm: string): string {
  return hhmm.slice(0, 5);
}

/**
 * Detect a shift's type using the nursery's configured opening/closing times.
 *
 * Rules (B4-01):
 * - Full Day: covers more than 75% of total operating hours
 * - Starter: starts within 90 minutes of opening time
 * - Finisher: ends within 90 minutes of closing time
 * - Mid-Day: starts after the Starter window and ends before the Finisher window
 */
export function detectShiftType(
  startTime: string,
  endTime: string,
  hours: OperatingHours = activeHours
): ShiftType {
  const s = toMinutes(startTime);
  const e = toMinutes(endTime);
  const open = toMinutes(hours.open);
  const close = toMinutes(hours.close);
  const totalSpan = Math.max(1, close - open);
  const shiftSpan = Math.max(0, e - s);

  if (shiftSpan / totalSpan > 0.75) return "full_day";

  const isStarter = Math.abs(s - open) <= 90;
  const isFinisher = Math.abs(close - e) <= 90;

  if (isStarter && isFinisher) return "full_day";
  if (isStarter) return "starter";
  if (isFinisher) return "finisher";
  return "mid_day";
}

/** Get full info for a shift type */
export function getShiftTypeInfo(type: ShiftType): ShiftTypeInfo {
  return { type, ...TYPES[type] };
}

/** Get info for a shift, accounting for optional manual override */
export function getShiftInfo(
  startTime: string,
  endTime: string,
  override?: string | null,
  hours: OperatingHours = activeHours
): ShiftTypeInfo {
  const type = override ? (override as ShiftType) : detectShiftType(startTime, endTime, hours);
  return getShiftTypeInfo(type);
}

/** All visible shift types (excluding day_off) for legend */
export const LEGEND_TYPES: ShiftTypeInfo[] = [
  getShiftTypeInfo("starter"),
  getShiftTypeInfo("finisher"),
  getShiftTypeInfo("mid_day"),
  getShiftTypeInfo("full_day"),
];

/**
 * B4-02: Shift time pickers are fully uncapped. There are NO minimum or
 * maximum time restrictions enforced by the platform. The only invariant
 * is that the end time must be after the start time.
 *
 * The `isOps` and `hours` parameters are accepted for backwards
 * compatibility but are intentionally ignored, operating hours are used
 * only for classification labels and notifications, never for input
 * validation.
 */
export function validateShiftTimes(
  start: string,
  end: string,
  _isOps: boolean = false,
  _hours: OperatingHours = activeHours
): string | null {
  const s = start.slice(0, 5);
  const e = end.slice(0, 5);
  if (!s || !e) return "Start and end time are required";
  if (e <= s) return "End time must be after start time";
  return null;
}

/**
 * Kept for backwards compatibility. Always returns true now, operating
 * hours never restrict what a user can enter (B4-02).
 */
export function isWithinOperatingHours(_time: string): boolean {
  return true;
}

/**
 * Calculate hours from HH:MM times.
 * A finish time that is earlier than the start time means the shift runs past
 * midnight (for example a 22:00 to 06:00 night shift), so the finish is treated
 * as the following morning instead of producing negative hours.
 */
export function calcHours(start: string, end: string): number {
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  return Math.round((mins / 60) * 10) / 10;
}

